import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import toast, { Toaster } from 'react-hot-toast';
import { sanitizeInput } from '../../services/securityService';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!email) {
      toast?.error('Please enter your email address');
      return;
    }

    setIsLoading(true);

    try {
      const sanitizedEmail = sanitizeInput(email);

      const { error } = await supabase?.auth?.resetPasswordForEmail(sanitizedEmail, {
        redirectTo: `${window?.location?.origin}/reset-password`,
      });

      if (error) {
        toast?.error(error?.message || 'Failed to send reset email');
        setIsLoading(false);
        return;
      }

      setEmailSent(true);
      toast?.success('Password reset email sent! Check your inbox.');
    } catch (error) {
      console.error('Password reset error:', error);
      toast?.error('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/10 via-background to-secondary/10 px-4">
      <Toaster position="top-center" />
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img src="/assets/images/logo.png" alt="FireCaseIQ" className="h-16 object-contain mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-foreground mb-2">
            {emailSent ? 'Check Your Email' : 'Reset Password'}
          </h1>
          <p className="text-foreground-secondary">
            {emailSent
              ? 'We\'ve sent you a password reset link' :'Enter your email to receive a password reset link'}
          </p>
        </div>

        {/* Form */}
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-border">
          {!emailSent ? (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                  Email Address
                </label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e?.target?.value)}
                  placeholder="you@example.com"
                  disabled={isLoading}
                  className="w-full"
                />
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-primary text-primary-foreground py-3 rounded-lg font-medium hover:bg-primary/90 transition-smooth disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Sending...' : 'Send Reset Link'}
              </Button>
            </form>
          ) : (
            <div className="text-center space-y-4">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
                <Icon name="Mail" className="w-8 h-8 text-green-600" />
              </div>
              <p className="text-foreground-secondary">
                We've sent a password reset link to <strong className="text-foreground">{email}</strong>
              </p>
              <p className="text-sm text-foreground-secondary">
                Click the link in the email to reset your password. The link will expire in 1 hour.
              </p>
              <Button
                onClick={() => {
                  setEmailSent(false);
                  setEmail('');
                }}
                className="w-full bg-primary text-primary-foreground py-3 rounded-lg font-medium hover:bg-primary/90 transition-smooth mt-4"
              >
                Send Another Email
              </Button>
            </div>
          )}

          <div className="mt-6 text-center">
            <Link to="/login" className="text-sm text-primary hover:text-primary/80 font-medium">
              Back to Login
            </Link>
          </div>
        </div>

        {/* Back to Home */}
        <div className="text-center mt-6">
          <Link to="/" className="text-sm text-foreground-secondary hover:text-foreground flex items-center justify-center">
            <Icon name="ArrowLeft" className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;